import styled, { css } from 'styled-components';
import type { MouseEvent, Ref } from 'react';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  colors,
  easingTokens,
  radius,
  shadows,
  space,
  transitionTokens,
} from '@ardoq/design-tokens';
import { ChevronIcon, ICON_SELECTOR, IconName } from '@ardoq/icons';
import { backToSlide } from './actions';
import { Markdown } from 'atomicComponents/Markdown';
import { IconButton, SmallGhostButton, UnstyledButton } from '@ardoq/button';
import { Text, text1Bold, text2, typographyMixins } from '@ardoq/typography';
import { Box } from '@ardoq/layout';
import type { TourAnchorId } from '@ardoq/product-tours';

const PRESENTATION_PANEL_MAX_WIDTH = '900px';
const SLIDE_OVERVIEW_WIDTH = '340px';
const DESCRIPTION_SIDEBAR_WIDTH = '380px';
const BAR_HEIGHT = 52;
const BAR_PROGRESS_HEIGHT = 2;
const SIDEBAR_OPEN_DURATION = '300ms';
const SIDEBAR_CLOSE_DURATION = '150ms';

export const NavigatorBar = styled.div`
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${space.xxsmall};
  width: 100%;
  min-height: ${BAR_HEIGHT}px;
  padding: 0 ${space.medium};
  background-color: ${colors.surfaceDefaultSubtle};
  border-top: 1px solid ${colors.borderSubtle};

  @media print {
    display: none;
  }
`;

export const PRESENTATION_NAVIGATOR_SELECTOR = NavigatorBar.toString();

export const CollapsedNavigatorSlot = styled.div`
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  height: 0;

  @media print {
    display: none;
  }
`;

const surfacePanelCSS = `
  background-color: ${colors.surfaceDefaultSubtle};
  border-radius: ${radius.large};
  box-shadow: ${shadows.shadowL};
  max-width: min(${PRESENTATION_PANEL_MAX_WIDTH}, 100%);
`;

// The sidebar fold icons point sideways for a panel at the edge of the screen.
// The navigator is a bar along the bottom, so the glyph is turned a quarter
// turn: the arrow points down to hide it and up to show it again.
const quarterTurnedIcon = css`
  ${ICON_SELECTOR} {
    transform: rotate(-90deg);
  }
`;

const CollapsedNavigatorButton = styled(IconButton)`
  position: absolute;
  bottom: ${space.medium};
  right: ${space.medium};
  ${quarterTurnedIcon};

  && {
    background-color: ${colors.surfaceDefaultSubtle};
    border-radius: ${radius.full};
    box-shadow: ${shadows.shadowL};

    &:hover {
      background-color: ${colors.surfaceSelected};
    }
  }
`;

const QuarterTurnedIconButton = styled(IconButton)`
  ${quarterTurnedIcon};
`;

type NavigatorToggleButtonProps = {
  label: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  ref: Ref<HTMLButtonElement>;
};

export const ShowNavigatorButton = ({
  label,
  onClick,
  ref,
}: NavigatorToggleButtonProps) => (
  <CollapsedNavigatorButton
    ref={ref}
    iconName={IconName.SIDEBAR_OPEN}
    label={label}
    onClick={onClick}
    dataTestId="presentation-show-navigator"
  />
);

type HideNavigatorButtonProps = NavigatorToggleButtonProps & {
  tourAnchorId: TourAnchorId;
};

export const HideNavigatorButton = ({
  label,
  onClick,
  ref,
  tourAnchorId,
}: HideNavigatorButtonProps) => (
  <QuarterTurnedIconButton
    ref={ref}
    data-tour-id={tourAnchorId}
    iconName={IconName.SIDEBAR_CLOSE}
    label={label}
    onClick={onClick}
    dataTestId="presentation-hide-navigator"
  />
);

// Spans the full bar so it reads as the presentation's progress rather than a
// control inside it.
const SlideProgressTrack = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${BAR_PROGRESS_HEIGHT}px;
  overflow: hidden;
  background-color: ${colors.borderSubtle};
`;

const SlideProgressFill = styled.div<{ $fraction: number }>`
  height: 100%;
  width: ${({ $fraction }) => `${$fraction * 100}%`};
  background-color: ${colors.icon};
  transition: width ${transitionTokens.medium} ${easingTokens.cubicOut};
`;

type SlideProgressProps = {
  currentSlideNumber: number;
  slideCount: number;
};

export const SlideProgress = ({
  currentSlideNumber,
  slideCount,
}: SlideProgressProps) => (
  <SlideProgressTrack aria-hidden="true">
    <SlideProgressFill
      $fraction={slideCount === 0 ? 0 : currentSlideNumber / slideCount}
    />
  </SlideProgressTrack>
);

export const NavigatorDivider = styled.div`
  flex-shrink: 0;
  width: 1px;
  height: 20px;
  margin: 0 ${space.xxsmall};
  background-color: ${colors.borderSubtle};
`;

export const NavigatorPresentationName = styled(Box)`
  flex: 1;
  min-width: 0;
`;

export const SlidePosition = styled(Text)`
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
`;

export const DescriptionSidebar = styled.aside<{ $isOpen: boolean }>`
  flex-shrink: 0;
  overflow: hidden;
  width: ${({ $isOpen }) => ($isOpen ? DESCRIPTION_SIDEBAR_WIDTH : '0')};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  background-color: ${colors.surfaceDefaultSubtle};
  border-left: ${({ $isOpen }) => ($isOpen ? '1px' : '0')} solid
    ${colors.borderSubtle};
  transition-property: width, border-left-width, visibility;
  transition-duration: ${({ $isOpen }) =>
    $isOpen ? SIDEBAR_OPEN_DURATION : SIDEBAR_CLOSE_DURATION};
  transition-timing-function: ease-in;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  table {
    thead {
      background-color: ${colors.surfaceHover};
    }
    tbody tr {
      background-color: ${colors.surfaceDefault};
      &:nth-of-type(odd) {
        background-color: ${colors.surfaceDefaultSubtle};
      }
    }
  }

  @media print {
    display: none;
  }
`;

export const DescriptionSidebarContent = styled.div`
  width: ${DESCRIPTION_SIDEBAR_WIDTH};
  height: 100%;
  overflow: auto;
  padding: ${space.medium};
`;

export const SLIDE_OVERVIEW_PANEL_ID = 'presentation-slide-overview-panel';

export const SlideOverviewPanel = styled.div`
  ${surfacePanelCSS};
  margin-bottom: ${space.small};
  width: ${SLIDE_OVERVIEW_WIDTH};
  max-height: 50vh;
  overflow: auto;
  padding: ${space.small};
`;

export const SlideOverviewItemButton = styled(UnstyledButton)<{
  $isActive: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${space.medium};
  width: 100%;
  padding: ${space.small} ${space.medium};
  border-radius: ${radius.medium};
  background-color: ${({ $isActive }) =>
    $isActive ? colors.surfaceSelected : 'transparent'};

  &:hover {
    background-color: ${({ $isActive }) =>
      $isActive ? colors.surfaceSelected : colors.surfaceHover};
  }
`;

export const SlideOverviewItemNumber = styled(Text)`
  flex-shrink: 0;
  min-width: 24px;
  font-variant-numeric: tabular-nums;
`;

export const SlideOverviewItemTitle = styled(Text)`
  flex: 1;
`;

// The Markdown component colours blockquotes, list markers and table headings
// with tokens meant for a light surface. It also hard-codes a font size per
// heading level, which is too large for a panel this size, so each level is
// remapped a step down.
export const SlideMarkdown = styled(Markdown)`
  && {
    *,
    *::marker {
      color: inherit;
    }

    a {
      color: ${colors.textAction};
    }

    h1 {
      ${typographyMixins.header2};
    }
    h2 {
      ${typographyMixins.header3};
    }
    h3 {
      ${typographyMixins.header4};
    }
    h4 {
      ${text1Bold};
    }
    h5,
    h6 {
      ${typographyMixins.header5};
    }

    h1,
    h2,
    h3 {
      margin: ${space.medium} 0 ${space.xsmall};
      padding-bottom: 0;
    }
    h4,
    h5,
    h6 {
      margin: ${space.small} 0 ${space.xxsmall};
    }

    p,
    ul,
    ol,
    table {
      ${text2};
    }

    /* MarkdownViewer renders its content in a nested wrapper */
    > * > :first-child {
      margin-top: 0;
    }
  }
`;

const BackToSlideButton = styled(SmallGhostButton)`
  flex-shrink: 0;
`;

export const BackToSlide = ({ label }: { label: string }) => (
  <BackToSlideButton onClick={() => dispatchAction(backToSlide())}>
    <ChevronIcon direction="left" />
    {label}
  </BackToSlideButton>
);
